import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, ListGroup, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { getTournaments, createTournament } from '../../services/api';
import { Tournament } from '../../models/Tournament';

const TournamentList: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTournamentName, setNewTournamentName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getTournaments();
      setTournaments(data);
    } catch (err) {
      console.error('Error fetching tournaments:', err);
      setError('Failed to load tournaments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTournament = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newTournamentName.trim()) {
      return;
    }

    setCreating(true);

    try {
      const newTournament = await createTournament(newTournamentName);
      setTournaments([...tournaments, newTournament]);
      setNewTournamentName('');
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating tournament:', err);
      setError('Failed to create tournament. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Tournaments</h2>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          Create Tournament
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : tournaments.length > 0 ? (
        <ListGroup>
          {tournaments.map((tournament) => (
            <ListGroup.Item key={tournament.id}>
              <div style={{ position: 'relative', paddingBottom: '25px' }}>
                <div className="d-flex justify-content-between">
                  <h5 className="mb-0">{tournament.name}</h5>
                  <Link to={`/tournaments/${tournament.tournamentId}`} className="btn btn-outline-primary">
                    View Details
                  </Link>
                </div>
                <div style={{ position: 'absolute', left: '0', bottom: '0' }}>
                  <small className="text-muted">Last updated: {new Date(tournament.lastUpdated).toLocaleString()}</small>
                </div>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      ) : (
        <Card className="text-center p-5">
          <Card.Body>
            <Card.Title>No Tournaments Found</Card.Title>
            <Card.Text>
              Create a new tournament to get started.
            </Card.Text>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              Create Tournament
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Create Tournament Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Tournament</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateTournament}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Tournament Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter tournament name"
                value={newTournamentName}
                onChange={(e) => setNewTournamentName(e.target.value)}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={creating}>
              {creating ? 'Creating...' : 'Create Tournament'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default TournamentList;
